import React, { useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';

import { useGetInvestmentFundsQuery } from 'api/main';
import { ORDER_BY, sortDirection } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import FundRow from './FundRow';
import {
    EmptyTableBody,
    CustomTablePagination,
    EnhancedTableHead,
    ActionButton,
    ListContainer,
} from 'components';
import {
    TableContainer,
    Table,
    TableRow,
    TableBody,
    TableFooter,
    Typography,
    Box,
} from '@mui/material';

const tableHeads = [
    { id: ORDER_BY.name, width: 20, isSortable: true, label: texts.FUND_NAME },
    { id: ORDER_BY.type, width: 8, isSortable: true, label: texts.FUND_TYPE },
    { id: ORDER_BY.releaseDate, width: 15, isSortable: true, label: texts.COMMERCIALISATION_DATE },
    {
        id: ORDER_BY.commissionControlDate,
        width: 14,
        isSortable: true,
        label: texts.COMMISSION_CONTROL_DATE,
    },
    { id: ORDER_BY.closingDate, width: 15, isSortable: false, label: texts.DATE_CLOSING },
    { id: 'open', width: 8, isSortable: false, label: texts.OPEN },
    { id: 'visibility', width: 8, isSortable: false, label: texts.VISIBILITY },
    { id: 'privacy', width: 8, isSortable: false, label: texts.PRIVACY },
    { id: 'actions', width: 4, isSortable: false, label: '' },
];

const FundManagementTable = () => {
    const dispatch = useDispatch();
    const { path } = useRouteMatch();

    const [filters, setFilters] = useState({
        page: 1,
        orderBy: ORDER_BY.id,
        direction: sortDirection.DESC,
    });

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        [`order[${filters.orderBy}]`]: filters.direction,
    });

    const handlePageChange = page => () => {
        setFilters(oldValue => ({ ...oldValue, page }));
    };

    const handleSort = (property, direction) => {
        setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
    };

    return (
        <ListContainer>
            <Box display="flex" justifyContent="space-between">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.FUND}
                </Typography>
                <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                    <ActionButton
                        variant="outlined"
                        onClick={() => dispatch(push(`${path}/create`))}
                    >
                        {texts.CREATE_INVESTMENT_FUND}
                    </ActionButton>
                </Box>
            </Box>
            <TableContainer sx={{ mt: '30px' }}>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {tableHeads.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={tableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSort}
                    />
                    {fundsLoader || !fundList.length ? (
                        <EmptyTableBody colSpan={tableHeads.length} loading={fundsLoader} />
                    ) : (
                        <TableBody>
                            {fundList?.map(fund => (
                                <FundRow key={fund.id} fund={fund} />
                            ))}
                        </TableBody>
                    )}

                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={1} // will manage multiple pages when required
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </ListContainer>
    );
};

export default FundManagementTable;
